import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'

import { useAdminContext } from '../session'
import type { AdminEvent, Readiness } from '../types'
import { EditionCreateForm } from './EditionCreateForm'
import { EditionDuplicateForm } from './EditionDuplicateForm'
import { EditionLifecycle } from './EditionLifecycle'
import { EditionStatusBadge, formatInZone } from './editionFields'

type Edition = ReturnType<typeof useAdminContext>['editions'][number]

/**
 * Toutes les éditions, et ce qu'on peut faire de celle qu'on regarde.
 *
 * Avant cet écran, créer l'édition de l'an prochain, la publier ou archiver
 * l'ancienne passait par des appels à l'API : un geste annuel, donc oublié
 * d'une année sur l'autre, donc fait de travers. On le met là où l'on règle
 * l'édition, avec ses règles écrites à côté des boutons.
 *
 * Un seul formulaire ouvert à la fois : création et duplication remplissent
 * chacune un écran de téléphone, les empiler ferait perdre le fil.
 */
export function EditionsPanel({
  current,
  readiness,
}: {
  current: AdminEvent
  readiness: Readiness | undefined
}) {
  const { t, i18n } = useTranslation()
  const { editions, selectEvent, can, eventId } = useAdminContext()
  const [open, setOpen] = useState<'create' | 'duplicate' | null>(null)
  const [created, setCreated] = useState<AdminEvent | null>(null)

  const mayCreate = can('event.event.create')
  const mayDuplicate = can('event.event.duplicate')

  function nameOf(names: Record<string, string>, fallback: string): string {
    return names[i18n.language] || Object.values(names).find(Boolean) || fallback
  }

  function switchTo(id: string) {
    setOpen(null)
    setCreated(null)
    selectEvent(id)
    // On remonte : le formulaire de l'autre édition commence en haut de page.
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // La liste vient de la session ; tant qu'elle n'est pas arrivée (ou pour un
  // compte qui ne voit que son édition), on montre au moins celle-ci.
  const rows: Edition[] =
    editions.length > 0
      ? [...editions].sort((a, b) => b.edition_year - a.edition_year || b.starts_at.localeCompare(a.starts_at))
      : [
          {
            id: current.id,
            slug: current.slug,
            edition_year: current.edition_year,
            status: current.status,
            name_i18n: current.name_i18n,
            timezone: current.timezone,
            currency: current.currency,
            starts_at: current.starts_at,
          } as Edition,
        ]

  return (
    <section className="rounded-lg border border-line-subtle bg-bg-elevated p-4 sm:p-6">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.editions.title')}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.editions.intro')}</p>

      <ul className="mt-4 divide-y divide-line-subtle rounded-md border border-line-subtle">
        {rows.map((edition) => {
          const selected = edition.id === current.id
          return (
            <li
              key={edition.id}
              className={cn(
                'flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between',
                selected && 'bg-bg-overlay/40',
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display tabular-nums text-champagne-300">
                    {edition.edition_year}
                  </span>
                  <span className="min-w-0 break-words text-sm text-content-primary">
                    {nameOf(edition.name_i18n, edition.slug)}
                  </span>
                  <EditionStatusBadge status={edition.status} />
                </div>
                <p className="mt-0.5 break-all text-xs text-content-muted">
                  {edition.slug}
                  {' · '}
                  {formatInZone(edition.starts_at, edition.timezone, i18n.language)}
                  {' '}
                  <span className="whitespace-nowrap">({edition.timezone})</span>
                </p>
              </div>
              {selected ? (
                <span className="shrink-0 text-xs uppercase tracking-wide text-champagne-300">
                  {t('admin.editions.current')}
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 self-start sm:self-auto"
                  onClick={() => switchTo(edition.id)}
                >
                  {t('admin.editions.switch')}
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      <div className="mt-4">
        <EditionLifecycle event={current} readiness={readiness} can={can} />
      </div>

      {created ? (
        <div className="mt-4 rounded-md border border-state-success/30 bg-state-success/5 p-4">
          <p className="text-sm text-content-primary">
            {t('admin.editions.create.done', {
              name: nameOf(created.name_i18n, created.slug),
              year: created.edition_year,
            })}
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Button size="sm" onClick={() => switchTo(created.id)}>
              {t('admin.editions.openNew')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>
              {t('admin.editions.close')}
            </Button>
          </div>
        </div>
      ) : null}

      {(mayCreate || mayDuplicate) && open === null ? (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          {mayDuplicate ? (
            <Button size="sm" variant="outline" onClick={() => setOpen('duplicate')}>
              {t('admin.editions.duplicate.open')}
            </Button>
          ) : null}
          {mayCreate ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCreated(null)
                setOpen('create')
              }}
            >
              {t('admin.editions.create.open')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {open === 'create' ? (
        <div className="mt-4">
          <EditionCreateForm
            eventId={eventId}
            onCancel={() => setOpen(null)}
            onCreated={(event) => {
              setOpen(null)
              setCreated(event)
            }}
          />
        </div>
      ) : null}

      {open === 'duplicate' ? (
        <div className="mt-4">
          {/* `key` : changer d'édition repart des suggestions de la nouvelle
              source, pas de celles de la précédente. */}
          <EditionDuplicateForm
            key={current.id}
            source={current}
            eventId={eventId}
            onSwitch={switchTo}
            onCancel={() => setOpen(null)}
          />
        </div>
      ) : null}
    </section>
  )
}
